import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  deleteSaveTheDate,
  getUserSaveTheDates,
} from "../../utils/saveTheDatesApi";
import "../Invitations/Invitations.css";
import "./SaveTheDates.css";

const SaveTheDatesDashboard = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyMessage, setCopyMessage] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const hasSaveTheDate = items.length > 0;
  const firstSaveTheDate = hasSaveTheDate ? items[0] : null;
  const firstSaveTheDateId =
    firstSaveTheDate?.id || firstSaveTheDate?.save_the_date_id || null;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserSaveTheDates();
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setError(err.message || "Настана грешка при вчитување на податоците.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleCopyLink = async (slug) => {
    try {
      const shareUrl = `${window.location.origin}/save-the-dates/${slug}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopyMessage("Линкот е копиран во clipboard.");
      setTimeout(() => setCopyMessage(null), 3000);
    } catch (err) {
      console.error("Clipboard error:", err);
      setCopyMessage("Неуспешно копирање на линк.");
      setTimeout(() => setCopyMessage(null), 3000);
    }
  };

  const handleDelete = async (saveTheDateId) => {
    const confirmed = window.confirm(
      "Сигурни сте дека сакате да го избришете Save the Date?"
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeleteLoadingId(saveTheDateId);
      await deleteSaveTheDate(saveTheDateId);
      setItems((prev) =>
        prev.filter((item) => {
          const itemId = item.id || item.save_the_date_id;
          return itemId !== saveTheDateId;
        })
      );
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно бришење на Save the Date.");
    } finally {
      setDeleteLoadingId(null);
    }
  };

  if (loading) {
    return (
      <Box className="invitation-page-container">
        <CircularProgress />
        <Typography mt={2}>Вчитување...</Typography>
      </Box>
    );
  }

  return (
    <Box className="invitation-page-container">
      <Stack spacing={2} alignItems="center" className="invitation-header">
        <Tabs
          value={1}
          onChange={(_, value) => {
            if (value === 0) {
              navigate("/invitations");
            }
          }}
          aria-label="invitation tabs"
          TabIndicatorProps={{ style: { backgroundColor: "var(--primary-color)" } }}
          sx={{
            ".MuiTabs-flexContainer": { gap: 2 },
            ".MuiTab-root": {
              textTransform: "none",
              color: "#7a7a7a",
              fontWeight: 500,
              "&.Mui-selected": {
                color: "var(--primary-color)",
                fontWeight: 700,
              },
            },
          }}
        >
          <Tab label="Покани" />
          <Tab label="Save the Date" />
        </Tabs>
        <Typography variant="h4" component="h1" textAlign="center">
          Save The Date
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Креирајте Save the Date линк, споделете го и најавете го вашиот голем
          ден со две фотографии.
        </Typography>
        <Button
          component={RouterLink}
          to="/save-the-dates/new"
          variant="contained"
          disabled={hasSaveTheDate}
          sx={{
            backgroundColor: "var(--primary-color)",
            "&:hover": { backgroundColor: "var(--primary-color-dark)" },
            opacity: hasSaveTheDate ? 0.6 : 1,
          }}
          title={
            hasSaveTheDate
              ? "Дозволен е само еден Save the Date. Уредете го постоечкиот."
              : undefined
          }
        >
          Нов Save The Date
        </Button>
        {hasSaveTheDate && firstSaveTheDateId ? (
          <Button
            component={RouterLink}
            to={`/save-the-dates/${firstSaveTheDateId}/edit`}
            variant="text"
            sx={buttonSx}
          >
            Уреди го постоечкиот
          </Button>
        ) : null}
        {copyMessage && <Alert severity="info">{copyMessage}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>

      <Box className="invitation-section">
        <Typography variant="h5" component="h2" gutterBottom>
          Ваши Save The Date
        </Typography>

        {items.length === 0 ? (
          <Typography color="text.secondary">
            Сѐ уште немате креирано Save the Date.
          </Typography>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {items.map((entry, index) => {
              const persistentId = entry.id || entry.save_the_date_id || null;
              const entryId = persistentId || String(index);
              const entrySlug = entry.slug || "";
              const shareUrl = entrySlug
                ? `${window.location.origin}/save-the-dates/${entrySlug}`
                : "-";
              const coverImage =
                entry.cover_image_url || entry.cover_url || null;
              return (
                <Grid item xs={12} md={6} lg={4} key={entryId}>
                  <Card className="invitation-card">
                    {coverImage && (
                      <CardMedia
                        component="img"
                        height="180"
                        image={coverImage}
                        alt={entry.title || "Save The Date"}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6">
                        {entry.title || "Save The Date"}
                      </Typography>
                      {entry.event_date && (
                        <Typography variant="body2" color="text.secondary">
                          {new Date(entry.event_date).toLocaleString()}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        className="invitation-share-link"
                      >
                        {shareUrl}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => handleCopyLink(entrySlug)}
                        disabled={!entrySlug}
                        sx={buttonSx}
                      >
                        Копирај линк
                      </Button>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/save-the-dates/${entrySlug}`}
                        target="_blank"
                        disabled={!entrySlug}
                        sx={buttonSx}
                      >
                        Преглед
                      </Button>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={persistentId ? `/save-the-dates/${entryId}/edit` : "#"}
                        disabled={!persistentId}
                        sx={buttonSx}
                      >
                        Уреди
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleDelete(entryId)}
                        disabled={deleteLoadingId === entryId || !persistentId}
                        startIcon={<DeleteIcon fontSize="small" />}
                        sx={buttonSx}
                      >
                        Избриши
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

const buttonSx = {
  color: "var(--primary-color)",
  "&:hover": {
    backgroundColor: "rgba(227, 95, 135, 0.08)",
  },
};

export default SaveTheDatesDashboard;
